import { CommonModule } from '@angular/common'
import { Component, Input } from '@angular/core'
import { RouterModule } from '@angular/router'
import { Observable } from 'rxjs'
import { Icon, IconColor, IconComponent } from '../icon'

export enum TabSelectorStyle {
  Large = 'large',
  Small = 'small',
}

/**
 * An individual tab selector option, displayed as a router link.
 */
export interface TabSelectorOption<TValue> {
  /**
   * The value of the option, also used to label it, if no explicit label is provided.
   */
  value: TValue

  /**
   * Optional label, which falls back to the <code>value</code> if not provided.
   */
  label?: string

  /**
   * Optional label, which falls back to the <code>value</code> if not provided.
   */
  mobileLabel?: string

  /**
   * Options used to construct the link for this option.
   */
  routeOptions: {
    link: string | any[]
    queryParams?: Record<string, any>
  }

  /**
   * Optional icon appearing before the label
   */
  icon?: { icon: Icon; colour: IconColor }
}

/**
 * A component used to select an option from the provided list of <code>options</code>. The component is styled as a tab
 * to be displayed on top of a section such as a card.
 */
@Component({
  selector: 'ftr-tab-selector',
  templateUrl: './tab-selector.component.html',
  styleUrls: ['./tab-selector.component.css'],
  standalone: true,
  imports: [CommonModule, IconComponent, RouterModule],
})
export class TabSelectorComponent<TValue = string> {
  /**
   * Available options to be selected.
   *
   * @see TabSelectorOption for information on how the options work
   */
  @Input() options: Observable<TabSelectorOption<TValue>[]>

  /**
   * Value to display as currently selected.
   */
  @Input() selectedValue: Observable<TValue>

  /**
   * Display style of the tabs
   */
  @Input() tabSelectorStyle: TabSelectorStyle = TabSelectorStyle.Large

  /**
   * Display icons before the tab label
   */
  @Input() showIcons = false
}
